import { Component, createRef, RefObject } from 'react';
import { ILobbyState } from '../../../Core/State/ILobbyState';
import { IRoomState } from '../../../Core/State/IRoomState';
import { IUserState } from '../../../Core/State/IUserState';
import { DataGrid, GridColDef, GridRowParams } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import RoomInformation from '../Common/RoomInformation';
import styles from '../../../assets/css/components/Lobby.module.css';
import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material';
import { createTheme, ThemeProvider, Tooltip, Box } from '@mui/material';
import { isMobile } from 'react-device-detect';
import desktopTableImageAof from '/images/table/desktop-game-table-aof.svg';
import mobileTableImageAof from '/images/table/mobile-game-table-aof.svg';
import mobileTableImage from '/images/table/mobile-game-table.svg';
import desktopTableImage from '/images/table/desktop-game-table.svg';
import playersIcon from '/images/lobby/players.svg';
import { t } from '../../../i18n';

interface LobbyProps {
    joinRoom: (roomId: string) => void;
    joinedRoomsState: any[];
    userState: IUserState;
    lobbyState: ILobbyState;
    selectedRoomId: string | null;
    sendChatMessage: (message: string) => void;
}

interface LobbyState {
    chatInput: string;
    selectedRoom: IRoomState | null;
    isDragging: boolean;
    lastClientY: number;
}

export default class DesktopCashLobby extends Component<LobbyProps, LobbyState> {
    private dataGridRef: RefObject<HTMLDivElement>;
    private lastClientY: number = 0;

    constructor(props: LobbyProps) {
        super(props);
        this.state = {
            chatInput: '',
            selectedRoom: null,
            isDragging: false,
            lastClientY: 0
        };
        this.dataGridRef = createRef<HTMLDivElement>();
    }

    darkTheme = createTheme({
        palette: {
            mode: "dark",
            primary: {
                main: "#F93751",
            },
        },
        typography: {
            fontFamily: 'Inter, serif',
        },
    });

    componentDidMount(): void {
        // Preload table images
        const tableImages = isMobile ? [mobileTableImage, mobileTableImageAof] : [desktopTableImage, desktopTableImageAof];
        tableImages.forEach((image: string) => {
            const img = new Image();
            img.src = image;
        });
    }

    componentDidUpdate(prevProps: LobbyProps) {
        if (this.state.selectedRoom && prevProps.lobbyState !== this.props.lobbyState) {
            const updatedRoom = this.props.lobbyState.rooms.find(room => room.roomId === this.state.selectedRoom?.roomId);
            if (updatedRoom) {
                this.setState({ selectedRoom: updatedRoom });
            }
        }
    }

    handleChatInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ chatInput: event.target.value });
    };

    handleSendMessage = () => {
        const message = this.state.chatInput.trim();
        if (message.length > 0) {
            this.props.sendChatMessage(message);
            this.setState({ chatInput: '' });
        }
    };

    handleRowClick = (params: GridRowParams) => {
        this.setState({ selectedRoom: params.row as IRoomState });
    };

    handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        // Prevent default to avoid text selection during drag
        e.preventDefault();
        this.lastClientY = e.clientY;
        this.setState({ isDragging: true });
    };

    handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        if (this.state.isDragging && this.dataGridRef.current) {
            const delta = this.lastClientY - e.clientY;
            
            // Find the virtual scroller element inside the DataGrid
            const virtualScroller = this.dataGridRef.current.querySelector('.MuiDataGrid-virtualScroller') as HTMLElement;
            if (virtualScroller) {
                virtualScroller.scrollTop += delta;
                this.lastClientY = e.clientY;
            }
        }
    };

    handleMouseUp = () => {
        this.setState({ isDragging: false });
    };

    handleMouseLeave = () => {
        this.setState({ isDragging: false });
    };

    render() {
        const user = this.props.userState;
        const filteredRooms = this.props.lobbyState.rooms.filter(room => room.type === 'texas');
        const columns: GridColDef<IRoomState>[] = [
            { field: 'name', headerName: t('DesktopCashLobby:columns.table'), headerClassName: styles.tableHeader, flex: 2, type: 'string', resizable: false },
            { field: 'type', headerName: t('DesktopCashLobby:columns.game'), headerClassName: styles.tableHeader, flex: 1, type: 'string', resizable: false,
                valueGetter: (params: any) => {
                    switch (params.value.toLowerCase()) {
                        case 'texas':
                            return t('DesktopCashLobby:gameTypes.holdem');
                        case 'aof':
                            return t('DesktopCashLobby:gameTypes.aof');
                        default:
                            return params.value.charAt(0).toUpperCase() + params.value.slice(1);
                    }
                },
                renderCell: (params) => {
                    const value = params.value?.toLowerCase();

                    switch (value) {
                        case 'texas':
                            return (
                                <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                    {params.value}
                                </Box>
                            );
                        case 'aof':
                            return (
                                <Box className={styles.typeLabel} sx={{ backgroundColor: '#57222E', color: '#FC5468' }}>
                                    {params.value}
                                </Box>
                            );
                        default:
                            return (
                                <Box className={styles.typeLabel} sx={{ backgroundColor: '#2B5056', color: '#4DDAA2' }}>
                                    {params.value}
                                </Box>
                            );
                    }
                }
            },
            { field: 'stakes', headerName: t('DesktopCashLobby:columns.stakes'), headerClassName: styles.tableHeader, flex: 1, type: 'string', resizable: false, valueGetter: (params: any) => `$${params.row.smallBlind} / $${params.row.bigBlind}` },
            { field: 'maxBuyIn', headerName: t('DesktopCashLobby:columns.buyIn'), headerClassName: styles.tableHeader, flex: 1, type: 'string', resizable: false,
                renderCell: (params: any) => {
                    const formatCurrency = (value: number) => {
                        return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: value % 1 === 0 ? 0 : 2,
                            maximumFractionDigits: 2
                        }).format(value);
                    };
                    return (
                        <Tooltip title={
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <span style={{ fontWeight: 'bold', marginBottom: '0.125rem' }}>{t('DesktopCashLobby:tooltips.buyIn')}</span>
                                <span>{t('DesktopCashLobby:tooltips.minBuyIn')}: {formatCurrency(params.row.minBuyIn)}</span>
                                <span>{t('DesktopCashLobby:tooltips.maxBuyIn')}: {formatCurrency(params.row.maxBuyIn)}</span>
                            </Box>
                        } placement="top" arrow>
                            <span>{`${formatCurrency(params.row.minBuyIn)} / ${formatCurrency(params.row.maxBuyIn)}`}</span>
                        </Tooltip>
                    );
                }
            },
            { field: 'seats', headerName: t('DesktopCashLobby:columns.players'), headerClassName: styles.tableHeader, flex: 1, type: 'number', resizable: false, valueGetter: (params: any) => `${Math.min(params.row.players.length, params.row.maxPlayers)} / ${params.row.maxPlayers}`, align: 'left', headerAlign: 'left', renderCell: (params) => {
                const { playerSatCount, minPlayers, maxPlayers } = params.row;

                let textColor = '#179F55';
                let tooltip = t('DesktopCashLobby:tooltips.openSeats');

                if (playerSatCount < minPlayers) {
                  textColor = '#FFC100';
                  tooltip = t('DesktopCashLobby:tooltips.needsPlayers', { count: minPlayers });
                } else if (playerSatCount === maxPlayers) {
                  textColor = '#FF004D';
                  tooltip = t('DesktopCashLobby:tooltips.tableFull');
                }
            
                return (
                    <Tooltip title={tooltip} placement="top" arrow>
                        <Box className={styles.playerCount} sx={{ '--color': textColor }}>
                            <img src={playersIcon} alt="Players Icon" style={{ width: 15, height: 13, marginRight: 4 }} />
                            {`${Math.min(params.row.players.length, params.row.maxPlayers)} / ${params.row.maxPlayers}`}
                        </Box>
                    </Tooltip>
                );
            } },
            {
                field: 'join',
                headerName: '',
                headerClassName: styles.tableHeader,
                cellClassName: styles.lastTableCell,
                type: 'actions',
                flex: 1.5,
                resizable: false,
                renderCell: (params: any) => (
                    <Button
                        variant="contained"
                        fullWidth
                        className={this.props.joinedRoomsState.some((room) => room.roomId === params.row.roomId) ? styles.openedButton : styles.joinButton}
                        style={{ marginRight: '1rem' }}
                        onClick={() => {
                            this.props.joinRoom(params.row.roomId);
                        }}
                    >
                        {this.props.joinedRoomsState.some((room) => room.roomId === params.row.roomId) ? t('DesktopCashLobby:buttons.tableOpened') : t('DesktopCashLobby:buttons.openTable')}
                    </Button>
                ),
                align: 'left',
                headerAlign: 'left',
            },
        ];

        const customDataGridClasses = {
            virtualScroller: styles.customScroller,
            columnHeaders: styles.tableHeaderRow,
            columnHeader: styles.tableHeaderCell,
            cell: styles.tableCell
        }

        return (
            <ThemeProvider theme={this.darkTheme}>
                <div style={{ display: 'flex', width: '100%', height: 'calc(100dvh - 140px)', gap: '1rem', padding: '1rem 1rem 0 1rem', boxSizing: 'border-box' }}>
                    <div
                        style={{ width: 'calc(100% - 1rem - 256px)', height: '100%', flexGrow: 0, flexShrink: 0, boxSizing: 'border-box' }}
                        ref={this.dataGridRef}
                        onMouseDown={this.handleMouseDown}
                        onMouseMove={this.handleMouseMove}
                        onMouseUp={this.handleMouseUp}
                        onMouseLeave={this.handleMouseLeave}
                    >
                        <DataGrid
                            disableColumnMenu
                            disableColumnFilter
                            getCellClassName={() => styles.tableCell}
                            getRowClassName={(params) => {
                                const userId = user?.id;
                                if (!userId) {
                                    return styles.tableRow;
                                }
                                const isUserPlaying = params.row.players.some((player: { userId: string }) => player.userId === user.id);
                                return isUserPlaying ? styles.active : styles.tableRow;
                            }}
                            rows={filteredRooms}
                            columns={columns}
                            classes={customDataGridClasses}
                            pageSizeOptions={[500]}
                            getRowId={(row) => row.roomId}
                            hideFooter
                            onRowClick={this.handleRowClick}
                            slots={{
                                columnSortedDescendingIcon: ArrowDropUp,
                                columnSortedAscendingIcon: ArrowDropDown,
                            }}
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: 'seats', sort: 'desc' }]
                                }
                            }}
                            sx={{
                                border: 'none',
                                color: 'white',
                                height: '100%',
                                cursor: this.state.isDragging ? 'grabbing' : 'grab',
                                '& .MuiDataGrid-virtualScroller': {
                                    '&::-webkit-scrollbar': {
                                        width: '0.4em',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: 'rgba(255,255,255,.3)',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: 'rgba(255,255,255,.5)',
                                    },
                                },
                                '& .MuiDataGrid-main': {
                                    gap: '0.25rem'
                                },
                                '& .MuiDataGrid-virtualScrollerRenderZone': {
                                    gap: '0.25rem',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    paddingBottom: '1rem'
                                },
                                '& .MuiDataGrid-root': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-columnHeader': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-cell': {
                                    fontFamily: 'Inter, serif'
                                },
                                '& .MuiDataGrid-cell:first-of-type': {
                                    borderLeft: '2px solid #F52740 !important',
                                },
                                '& .MuiDataGrid-row': {
                                    cursor: 'pointer',
                                    background: 'linear-gradient(90deg, rgba(245, 39, 64, 0.10) 0%, rgba(28, 28, 37, 0.10) 22.39%), #2A2A32',
                                    transition: 'background 300ms ease',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                },
                                '& .MuiDataGrid-row:hover': {
                                    cursor: 'pointer',
                                    background: '#40414C !important',
                                },
                                '& .MuiDataGrid-row:focus': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-row:active': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-sortIcon': {
                                    color: '#66666b',
                                    fontSize: '2rem'
                                },
                                '& .MuiDataGrid-row.Mui-selected': {
                                    backgroundColor: '#40414C',
                                },
                                '& .MuiDataGrid-columnSeparator:not(.MuiDataGrid-columnSeparator--resizable)': {
                                    display: 'none !important',
                                },
                            }}
                        />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', width: '256px', height: '100%', flexGrow: 0, flexShrink: 0, paddingBottom: '1rem', boxSizing: 'border-box' }}>
                        <RoomInformation
                            selectedRoom={this.state.selectedRoom}
                            joinRoom={this.props.joinRoom}
                            userState={this.props.userState}
                        />
                    </div>
                </div>
            </ThemeProvider>
        );
    }
}
